import * as React from "react";
import { Box, Stack, Typography, Button, IconButton, Select, MenuItem, CircularProgress, Tooltip } from "@mui/material";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import LockRoundedIcon from "@mui/icons-material/LockRounded";
import {
  deleteMissionMitigation,
  fetchMissionMitigations,
  insertMissionMitigation,
  updateMissionMitigation,
} from "../../../api/queries";
import { createPendingEdits, subscribeToRows, upsertById } from "../../../api/sheetSync";
import { bandForCell } from "../riskMatrixTheme";
import type { MissionMitigationRow, MissionRiskRow } from "../../../types/risk";
import { AuthorTip } from "../sheetAuthors";

const MONO = "'Roboto Mono', 'Courier New', monospace";

// Plain inline style (not MUI sx) -- real CSS property names only.
const textAreaStyle: React.CSSProperties = {
  width: "100%",
  boxSizing: "border-box",
  backgroundColor: "rgba(9,5,11,0.55)",
  border: "1px solid rgba(255,255,255,0.16)",
  borderRadius: 8,
  color: "#FFFFFF",
  fontFamily: "inherit",
  fontSize: 13,
  padding: "9px 12px",
  outline: "none",
  resize: "vertical",
  minHeight: 40,
};

function MitigationText(props: { value: string; onChange: (value: string) => void }) {
  const [text, setText] = React.useState(props.value);

  React.useEffect(() => setText(props.value), [props.value]);

  return (
    <textarea
      rows={1}
      placeholder="How is this risk mitigated?"
      value={text}
      onChange={(e) => {
        setText(e.target.value);
        props.onChange(e.target.value);
      }}
      style={textAreaStyle}
      onFocus={(e) => (e.currentTarget.style.borderColor = "#A78BFA")}
      onBlur={(e) => (e.currentTarget.style.borderColor = "rgba(255,255,255,0.16)")}
    />
  );
}

export default function MitigationTable(props: { risks: MissionRiskRow[] }) {
  const { risks } = props;
  const [mitigations, setMitigations] = React.useState<MissionMitigationRow[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [adding, setAdding] = React.useState(false);
  const reconciling = React.useRef(false);
  const saveTimers = React.useRef<Record<string, number>>({});
  const pending = React.useRef(createPendingEdits());

  const riskIds = React.useMemo(() => risks.map((r) => r.id), [risks]);
  const riskIdsKey = riskIds.join(",");

  // Drives auto row creation/removal -- red only, per spec.
  const redZoneRiskIds = React.useMemo(
    () => risks.filter((r) => r.matrix_cell && bandForCell(r.matrix_cell) === "very_high").map((r) => r.id),
    [risks]
  );
  const redZoneKey = redZoneRiskIds.join(",");

  // What a manually-added row's dropdown can pick from: red, orange (high)
  // or yellow (medium) -- green (low) and unplaced risks are excluded.
  const selectableRiskIds = React.useMemo(
    () =>
      risks
        .filter((r) => r.matrix_cell && bandForCell(r.matrix_cell) !== "low")
        .map((r) => r.id),
    [risks]
  );

  const riskById = React.useMemo(() => new Map(risks.map((r) => [r.id, r])), [risks]);

  // A risk already used by some row (auto or manual) can't be picked again
  // from another row's dropdown -- one mitigation entry per risk unless
  // that row is the one already holding it.
  const usedRiskIds = React.useMemo(() => new Set(mitigations.map((m) => m.risk_id)), [mitigations]);
  const availableToAdd = React.useMemo(
    () => selectableRiskIds.filter((id) => !usedRiskIds.has(id)),
    [selectableRiskIds, usedRiskIds]
  );

  // Loads every mitigation row for any risk of this group's sheet (not just
  // currently-red ones, so a manually added row whose risk has since left
  // the red zone doesn't just disappear), then keeps them in step with the
  // other members' edits. Reloads whenever the set of risks changes -- but
  // silently: the spinner only shows for the very first load, so a member
  // adding a risk doesn't pull the textarea out from under someone typing.
  React.useEffect(() => {
    let mounted = true;
    if (riskIds.length === 0) {
      setMitigations([]);
      setLoading(false);
      return;
    }

    const ownRiskIds = new Set(riskIds);

    const load = () =>
      fetchMissionMitigations(riskIds).then(({ data, error }) => {
        if (!mounted) return;
        setLoading(false);
        if (error) return;
        setMitigations((data ?? []).map((m) => pending.current.merge(m.id, m)));
      });

    // mission_mitigations has no group column to filter on; row-level
    // security already limits what arrives to this group's rows.
    const unsubscribe = subscribeToRows<MissionMitigationRow>(
      "mission_mitigations",
      undefined,
      (change) => {
        if (!mounted) return;
        if (change.type === "DELETE") {
          setMitigations((prev) => prev.filter((m) => m.id !== change.id));
          return;
        }
        if (!ownRiskIds.has(change.row.risk_id)) return;
        const row = pending.current.merge(change.row.id, change.row);
        setMitigations((prev) => upsertById(prev, row));
      },
      load
    );
    load();

    return () => {
      mounted = false;
      unsubscribe();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [riskIdsKey]);

  // Keeps rows in sync with red-zone membership -- a risk never ends up
  // with two rows: dragging a risk into red either creates a fresh row or,
  // if it already has one (e.g. a manually added yellow/orange entry),
  // locks that same row in place instead of inserting a duplicate.
  // Dragging it back out unlocks (not deletes) that row, so any mitigation
  // text already written is kept. Every member's browser runs this for the
  // same change; the database allows one row per risk, so a losing insert
  // just fails and the winner's row arrives through the subscription.
  React.useEffect(() => {
    if (loading || reconciling.current) return;

    const byRiskId = new Map(mitigations.map((m) => [m.risk_id, m]));
    const redSet = new Set(redZoneRiskIds);

    const toLock = redZoneRiskIds.filter((id) => {
      const existing = byRiskId.get(id);
      return !existing || !existing.is_auto;
    });
    const toUnlock = mitigations.filter((m) => m.is_auto && !redSet.has(m.risk_id));

    if (toLock.length === 0 && toUnlock.length === 0) return;

    reconciling.current = true;

    // Each result is folded into the current state rather than written back
    // as one snapshot, so rows that arrive from other members meanwhile
    // aren't dropped.
    const setAuto = (id: string, isAuto: boolean) => {
      pending.current.mark(id, { is_auto: isAuto });
      setMitigations((prev) => prev.map((m) => (m.id === id ? { ...m, is_auto: isAuto } : m)));
    };

    (async () => {
      for (const riskId of toLock) {
        const existing = byRiskId.get(riskId);

        if (existing) {
          const { error } = await updateMissionMitigation(existing.id, { is_auto: true });
          if (!error) setAuto(existing.id, true);
        } else {
          const { data, error } = await insertMissionMitigation(riskId, mitigations.length, true);
          if (!error && data) setMitigations((prev) => upsertById(prev, data));
        }
      }

      for (const row of toUnlock) {
        const { error } = await updateMissionMitigation(row.id, { is_auto: false });
        if (!error) setAuto(row.id, false);
      }

      reconciling.current = false;
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [redZoneKey, loading]);

  const setRow = (id: string, updater: (row: MissionMitigationRow) => MissionMitigationRow) => {
    setMitigations((prev) => prev.map((m) => (m.id === id ? updater(m) : m)));
  };

  const changeMitigation = (id: string, value: string) => {
    setRow(id, (m) => ({ ...m, mitigation: value }));
    pending.current.mark(id, { mitigation: value });
    window.clearTimeout(saveTimers.current[id]);
    saveTimers.current[id] = window.setTimeout(() => {
      updateMissionMitigation(id, { mitigation: value }).then(({ error }) => {
        if (error) console.error("Failed to save mitigation:", error.message);
      });
    }, 500);
  };

  const changeRisk = (id: string, riskId: string) => {
    setRow(id, (m) => ({ ...m, risk_id: riskId }));
    pending.current.mark(id, { risk_id: riskId });
    updateMissionMitigation(id, { risk_id: riskId }).then(({ error }) => {
      if (error) console.error("Failed to reassign mitigation:", error.message);
    });
  };

  const addRow = async () => {
    if (availableToAdd.length === 0) return;
    setAdding(true);
    const { data, error } = await insertMissionMitigation(availableToAdd[0], mitigations.length, false);
    setAdding(false);
    if (error || !data) return;
    setMitigations((prev) => upsertById(prev, data));
  };

  const removeRow = (id: string) => {
    setMitigations((prev) => prev.filter((m) => m.id !== id));
    pending.current.forget(id);
    window.clearTimeout(saveTimers.current[id]);
    deleteMissionMitigation(id).then(({ error }) => {
      if (error) console.error("Failed to delete mitigation:", error.message);
    });
  };

  if (loading) {
    return (
      <Stack alignItems="center" sx={{ py: 4 }}>
        <CircularProgress size={22} sx={{ color: "#C4B5FD" }} />
      </Stack>
    );
  }

  return (
    <Stack spacing={1.5}>
      <Stack spacing={0.5}>
        <Typography sx={{ color: "#FFFFFF", fontWeight: 800, fontSize: { xs: 16, sm: 17.5 } }}>
          Mitigation strategies
        </Typography>
        <Typography sx={{ color: "rgba(226,232,240,0.55)", fontSize: 12.5 }}>
          Red-zone risks automatically get a mitigation row that can't be removed. You can also add rows for orange or yellow
          risks -- green risks aren't eligible.
        </Typography>
      </Stack>

      {mitigations.length === 0 ? (
        <Typography sx={{ color: "rgba(226,232,240,0.5)", fontSize: 13 }}>
          No risks need mitigation yet.
        </Typography>
      ) : (
        <Stack spacing={1.25}>
          {mitigations.map((row) => {
            const risk = riskById.get(row.risk_id);

            return (
              <Box
                key={row.id}
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", sm: "170px 1fr auto" },
                  gap: 1.25,
                  alignItems: "start",
                  p: 1.25,
                  borderRadius: 2,
                  border: "1px solid rgba(163,54,54,0.4)",
                  bgcolor: "rgba(163,54,54,0.08)",
                }}
              >
                {row.is_auto ? (
                  <Stack direction="row" spacing={0.75} alignItems="center" sx={{ pt: { xs: 0, sm: 1 } }}>
                    <Tooltip title="Automatically placed -- this risk is in the red zone" arrow>
                      <LockRoundedIcon sx={{ fontSize: 14, color: "rgba(226,232,240,0.4)" }} />
                    </Tooltip>
                    <Typography sx={{ fontFamily: MONO, fontWeight: 800, fontSize: 13, color: "#FCA5A5" }}>
                      {risk?.risk_code || "?"}
                    </Typography>
                  </Stack>
                ) : (
                  <Select
                    size="small"
                    value={selectableRiskIds.includes(row.risk_id) ? row.risk_id : ""}
                    displayEmpty
                    onChange={(e) => changeRisk(row.id, e.target.value)}
                    sx={{
                      fontSize: 12.5,
                      fontFamily: MONO,
                      color: "#FFFFFF",
                      "& .MuiOutlinedInput-notchedOutline": { borderColor: "rgba(163,54,54,0.5)" },
                      "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "rgba(163,54,54,0.8)" },
                      "& .MuiSvgIcon-root": { color: "rgba(226,232,240,0.6)" },
                    }}
                    MenuProps={{ PaperProps: { sx: { bgcolor: "#1A1023", color: "#FFFFFF" } } }}
                  >
                    <MenuItem value="" disabled sx={{ fontSize: 12.5, fontStyle: "italic" }}>
                      {risk ? `${risk.risk_code} (no longer eligible)` : "Select a risk"}
                    </MenuItem>
                    {selectableRiskIds
                      .filter((id) => id === row.risk_id || !usedRiskIds.has(id))
                      .map((id) => (
                        <MenuItem key={id} value={id} sx={{ fontSize: 12.5 }}>
                          {riskById.get(id)?.risk_code || "?"}
                        </MenuItem>
                    ))}
                  </Select>
                )}

                <AuthorTip editedBy={row.edited_by} field="mitigation">
                  <MitigationText value={row.mitigation} onChange={(v) => changeMitigation(row.id, v)} />
                </AuthorTip>

                {!row.is_auto && (
                  <IconButton size="small" onClick={() => removeRow(row.id)} aria-label="Delete mitigation">
                    <DeleteOutlineRoundedIcon sx={{ fontSize: 17, color: "rgba(248,113,113,0.75)" }} />
                  </IconButton>
                )}
              </Box>
            );
          })}
        </Stack>
      )}

      <Button
        size="small"
        onClick={addRow}
        disabled={adding || availableToAdd.length === 0}
        startIcon={adding ? <CircularProgress size={14} sx={{ color: "inherit" }} /> : <AddRoundedIcon sx={{ fontSize: 16 }} />}
        sx={{
          alignSelf: "flex-start",
          textTransform: "none",
          fontWeight: 800,
          fontSize: 12.5,
          borderRadius: 1.5,
          color: "#FFFFFF",
          bgcolor: "#7C3AED",
          px: 1.5,
          "&:hover": { bgcolor: "#6D28D9" },
          "&.Mui-disabled": { bgcolor: "rgba(124,58,237,0.25)", color: "rgba(255,255,255,0.4)" },
        }}
      >
        Add row
      </Button>
    </Stack>
  );
}
